/**
 * Estructura de módulos y submódulos para el sidebar.
 * Importable desde otros componentes (ej: GestionUsuarios para configurar permisos).
 *
 * - Cada subItem puede declarar `group: 'Etiqueta'` para que Layout.jsx muestre
 *   un mini-divisor con esa etiqueta. Items sin `group` se muestran en bloque
 *   arriba del primer grupo.
 */
import {
  LayoutDashboard, FileText, FolderKanban, Users, Settings, Database,
  BarChart3, Info, Cog, Receipt, ShoppingCart, Warehouse, UserCog,
  TrendingUp, FileSpreadsheet, Truck, ClipboardList, DollarSign, Package,
  Cloud, Mail, Activity, ClipboardCheck, AlertTriangle, CreditCard, BookOpen,
  Send, RefreshCw, UtensilsCrossed, CalendarDays, Shield, ShieldCheck, HandCoins,
  PackageOpen, Wrench, Zap, BellRing, Inbox, HeartPulse, Printer, QrCode,
  MapPin, Wallet, CalendarClock, ShieldAlert, Lock, Briefcase, FileSearch, Landmark,
  CheckCircle, Bot, ArrowLeftRight,
  Plane,
} from 'lucide-react';

export const moduleItems = [
  // ─── Top-level: lo más usado a diario ───
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, type: 'single' },
  { path: '/aprobaciones', label: 'Bandeja de Aprobaciones', icon: Inbox, type: 'single' },
  { path: '/manual', label: 'Manual de Uso', icon: BookOpen, type: 'single' },

  // ─── Información ───
  {
    id: 'informacion',
    label: 'Información',
    icon: Info,
    type: 'module',
    subItems: [
      { path: '/ordenes-trabajo', label: 'Órdenes de Trabajo', icon: FileText, group: 'Producción' },
      { path: '/proyectos', label: 'Proyectos', icon: FolderKanban, group: 'Producción' },
      { path: '/clientes', label: 'Clientes', icon: Users, group: 'Personas' },
      { path: '/contactos', label: 'Contactos', icon: Users, group: 'Personas' },
      { path: '/directorio', label: 'Directorio Unificado', icon: Users, group: 'Personas' },
      { path: '/informacion/cotizador-historico', label: 'Cotizador Histórico', icon: DollarSign, action: 'cotizador_ver', group: 'Cotizaciones' },
    ],
  },

  // ─── Operaciones ───
  {
    id: 'operaciones',
    label: 'Operaciones',
    icon: Cog,
    type: 'module',
    subItems: [
      { path: '/reportes/informe-proyecto', label: 'Informe por Proyecto', icon: FileSpreadsheet, group: 'Reportes' },
      { path: '/reportes/informe-costo-ot', label: 'Informe Costo x OT', icon: FileSpreadsheet, group: 'Reportes' },
      { path: '/reportes', label: 'Reportes HH', icon: BarChart3, group: 'Reportes' },
      { path: '/reportes/gerencia', label: 'Informe Gerencial', icon: Mail, group: 'Reportes' },
      { path: '/operaciones/activo-circulante', label: 'Activo Circulante', icon: TrendingUp, group: 'Finanzas' },
    ],
  },

  // ─── Compras ───
  {
    id: 'compras',
    label: 'Compras',
    icon: ShoppingCart,
    type: 'module',
    subItems: [
      { path: '/compras/requisiciones', label: 'Pedidos / Requisiciones', icon: ClipboardList, group: 'Pedidos y Órdenes' },
      { path: '/compras/generar-oc', label: 'Generar OC', icon: FileText, action: 'oc_generar', group: 'Pedidos y Órdenes' },
      { path: '/compras/ordenes-compra', label: 'Listado OC', icon: FileText, group: 'Pedidos y Órdenes' },
      { path: '/compras/items-oc', label: 'Items de OCs', icon: Package, group: 'Pedidos y Órdenes' },
      { path: '/compras/seguimiento', label: 'Seguimiento OC', icon: Package, group: 'Pedidos y Órdenes' },
      { path: '/compras/firma-vb-lote', label: 'Firma VB por Lote', icon: ClipboardCheck, group: 'Pedidos y Órdenes' },
      { path: '/compras/rendiciones-gastos', label: 'Rendiciones de Gastos', icon: Wallet, group: 'Pedidos y Órdenes' },
      { path: '/compras/solicitar-cotizacion', label: 'Solicitar Cotización', icon: Send, action: 'oc_generar', group: 'Cotizaciones' },
      { path: '/compras/cotizaciones', label: 'Cotizaciones', icon: FileText, group: 'Cotizaciones' },
      { path: '/compras/proveedores', label: 'Proveedores', icon: Truck, action: 'proveedores_editar', group: 'Maestros' },
      { path: '/compras/buscador-materiales', label: 'Buscador de Materiales', icon: FileSearch, group: 'Maestros' },
      { path: '/compras/resumen-ots', label: 'Resumen OTs en Ejecución', icon: DollarSign, group: 'Maestros' },
      { path: '/compras/auditoria-trazabilidad', label: 'Auditoría Trazabilidad', icon: ShieldCheck, group: 'Maestros' },
      { path: '/procedimientos', label: 'Procedimientos', icon: BookOpen, group: 'Maestros' },
    ],
  },

  // ─── Bodega ───
  {
    id: 'bodega',
    label: 'Bodega',
    icon: Warehouse,
    type: 'module',
    subItems: [
      { path: '/bodega/dashboard', label: 'Dashboard Bodega', icon: BarChart3, group: 'Operación Diaria' },
      { path: '/bodega/recepcion-oc', label: 'Recepción OC', icon: Truck, group: 'Operación Diaria' },
      { path: '/bodega/recepcion-rapida', label: 'Recepción Rápida', icon: Zap, group: 'Operación Diaria' },
      { path: '/bodega/oc-pendientes-recepcion', label: 'OC Pendientes Recepción', icon: AlertTriangle, group: 'Operación Diaria' },
      { path: '/bodega/recepciones', label: 'Listado Recepciones', icon: ClipboardCheck, group: 'Operación Diaria' },
      { path: '/bodega/despachos', label: 'Despachos / Consumos', icon: PackageOpen, group: 'Operación Diaria' },
      { path: '/bodega/stock-insumos', label: 'Stock Insumos y EPP', icon: Package, group: 'Stock e Inventario' },
      { path: '/bodega/inventario-fisico', label: 'Inventario Físico', icon: ClipboardCheck, group: 'Stock e Inventario' },
      { path: '/bodega/inventario-semanal', label: 'Inventario Semanal', icon: QrCode, group: 'Stock e Inventario' },
      { path: '/bodega/precios-historicos', label: 'Precios Históricos', icon: TrendingUp, group: 'Stock e Inventario' },
      { path: '/bodega/herramientas', label: 'Herramientas en Cargo', icon: Wrench, group: 'Herramientas' },
      { path: '/bodega/maestro-herramientas', label: 'Maestro Herramientas', icon: Wrench, group: 'Herramientas' },
      { path: '/bodega/distribucion', label: 'Distribución Htas', icon: Activity, group: 'Herramientas' },
      { path: '/bodega/herramientas/timeline', label: 'Mapa de Herramienta', icon: MapPin, group: 'Herramientas' },
      { path: '/bodega/panoles', label: 'Pañoles', icon: Package, group: 'Herramientas' },
      { path: '/bodega/qr-imprimir', label: 'Imprimir QR Masivo', icon: Printer, group: 'Herramientas' },
      { path: '/bodega/maestro-materiales', label: 'Maestro Materiales', icon: Package, group: 'Maestros y Movimientos' },
      { path: '/bodega/transferencias', label: 'Transferencias', icon: Truck, group: 'Maestros y Movimientos' },
    ],
  },

  // ─── Contabilidad ───
  {
    id: 'contabilidad',
    label: 'Contabilidad',
    icon: Receipt,
    type: 'module',
    subItems: [
      { path: '/contabilidad/cuentas-pagar-alegra', label: 'Ctas por Pagar (Alegra)', icon: DollarSign, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/cuentas-pagar-rcv', label: 'Ctas por Pagar (Alegra+RCV)', icon: FileSpreadsheet, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/cxp-nominas', label: 'CxP Nóminas (Viernes)', icon: DollarSign, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/cheques-por-pagar', label: 'Cheques por Pagar', icon: FileText, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/cheques-emitidos', label: 'Cheques Emitidos', icon: CheckCircle, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/egresos', label: 'Egresos (Anticipos y Pagos)', icon: Wallet, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/regularizacion-anticipos?lado=egresos', label: 'Regularización de Anticipos', icon: ArrowLeftRight, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/seguros', label: 'Seguros (UF)', icon: ShieldCheck, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/solicitar-estado-cuenta', label: 'Solicitar Estado de Cuenta', icon: Mail, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/estado-cuenta-proveedor', label: 'Estado Cuenta Proveedor', icon: TrendingUp, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/conciliacion-proveedores', label: 'Conciliación Proveedores', icon: ShieldCheck, group: 'Pagos a Proveedores' },
      { path: '/contabilidad/tef-config', label: 'Cuentas Bancarias de Proveedores', icon: Landmark, group: 'Pagos a Proveedores', adminOnly: true },
      { path: '/cuentas-por-cobrar', label: 'Cuentas por Cobrar', icon: TrendingUp, group: 'Cobros a Clientes' },
      { path: '/contabilidad/ingresos', label: 'Ingresos (Recibos de Caja)', icon: HandCoins, group: 'Cobros a Clientes' },
      { path: '/contabilidad/regularizacion-anticipos?lado=ingresos', label: 'Regularización de Anticipos', icon: ArrowLeftRight, group: 'Cobros a Clientes' },
      { path: '/facturacion/proforma', label: 'Proforma Factura', icon: FileText, group: 'Cobros a Clientes' },
      { path: '/emision-dte', label: 'Emisión de DTE', icon: Receipt, group: 'Cobros a Clientes' },
      { path: '/facturacion/factura-venta', label: 'Factura de Venta', icon: FileText, group: 'Cobros a Clientes' },
      { path: '/facturacion/mantencion', label: 'Mantención Facturación', icon: ClipboardList, group: 'Cobros a Clientes' },
      { path: '/contabilidad/acepta-rechazo-dte', label: 'Acepta/Rechazo DTE', icon: FileText, group: 'DTE y SII' },
      { path: '/contabilidad/facturas-contabilizar', label: 'Facturas x Contabilizar', icon: DollarSign, group: 'DTE y SII' },
      { path: '/contabilidad/seguimiento-facturas', label: 'Seguimiento Facturas', icon: FileText, group: 'DTE y SII' },
      { path: '/contabilidad/registro-dte', label: 'Registro DTEs', icon: FileText, group: 'DTE y SII' },
      { path: '/contabilidad/rcv-import', label: 'Importar RCV (SII)', icon: FileSpreadsheet, group: 'DTE y SII' },
      { path: '/contabilidad/rcv-reportes', label: 'Reportes RCV', icon: TrendingUp, group: 'DTE y SII' },
      { path: '/contabilidad/duplicados', label: 'Facturas Duplicadas', icon: FileSearch, group: 'DTE y SII' },
      { path: '/contabilidad/notas-credito', label: 'Notas de Crédito', icon: FileText, group: 'DTE y SII' },
      { path: '/contabilidad/flujo-caja', label: 'Flujo de Caja', icon: TrendingUp, group: 'Tesorería' },
      { path: '/finanzas/conciliacion-bancaria', label: 'Conciliación Bancaria', icon: Landmark, group: 'Tesorería' },
      { path: '/finanzas/facturas-pendientes-recepcion', label: 'Pend. Recepción', icon: AlertTriangle, group: 'Tesorería' },
      { path: '/finanzas/pagos-bloqueados', label: 'Pagos Bloqueados', icon: Lock, group: 'Tesorería' },
      { path: '/finanzas/lotes-servicios', label: 'Lotes Servicios', icon: Briefcase, group: 'Tesorería' },
    ],
  },

  // ─── RRHH ───
  {
    id: 'rrhh',
    label: 'RRHH',
    icon: UserCog,
    type: 'module',
    subItems: [
      { path: '/rrhh', label: 'Dashboard RRHH', icon: BarChart3, group: 'Personal' },
      { path: '/rrhh/trabajadores', label: 'Trabajadores', icon: Users, group: 'Personal' },
      { path: '/rrhh/panel-personal', label: 'Panel de Personal', icon: Users, group: 'Personal' },
      { path: '/rrhh/control-hh', label: 'Control HH', icon: FileText, group: 'Asistencia y Control HH' },
      { path: '/rrhh/asistencia-mensual', label: 'Asistencia Mensual', icon: CalendarDays, group: 'Asistencia y Control HH' },
      { path: '/rrhh/tarjeta-control', label: 'Reloj Control', icon: CreditCard, group: 'Asistencia y Control HH' },
      { path: '/rrhh/parte-diario', label: 'Parte Diario Terreno', icon: ClipboardCheck, group: 'Asistencia y Control HH' },
      { path: '/rrhh/mis-partes', label: 'Mis Partes', icon: ClipboardList, group: 'Asistencia y Control HH' },
      { path: '/rrhh/mis-vb', label: 'Mis VB Supervisor', icon: ShieldCheck, group: 'Aprobaciones / Monitoreo' },
      { path: '/rrhh/monitor-partes', label: 'Monitor Partes', icon: Activity, group: 'Aprobaciones / Monitoreo' },
      { path: '/rrhh/partes-huerfanos', label: 'Partes Huérfanos', icon: AlertTriangle, adminOnly: true, group: 'Aprobaciones / Monitoreo' },
      { path: '/rrhh/control-errores', label: 'Control de Errores', icon: AlertTriangle, group: 'Aprobaciones / Monitoreo' },
      { path: '/rrhh/resumen-almuerzos', label: 'Resumen Almuerzos', icon: UtensilsCrossed, group: 'Casino' },
      { path: '/rrhh/resumen-casino', label: 'Resumen Casino', icon: BarChart3, group: 'Casino' },
      { path: '/rrhh/vacaciones', label: 'Vacaciones y Feriados', icon: Plane, group: 'Vacaciones y Feriados' },
      { path: '/rrhh/procedimientos', label: 'Procedimientos', icon: BookOpen, group: 'Otros' },
    ],
  },

  // ─── Alegra (Contabilidad) ───
  {
    id: 'alegra',
    label: 'Alegra (Contabilidad)',
    icon: Cloud,
    type: 'module',
    subItems: [
      { path: '/alegra', label: 'Sincronización', icon: Cloud },
      { path: '/alegra/rcv-comparator', label: 'Comparador RCV', icon: FileSpreadsheet },
      { path: '/alegra/proformas-pendientes', label: 'Proformas Pendientes', icon: FileText },
      { path: '/alegra/recepciones-pendientes', label: 'Recepciones Pendientes', icon: FileText },
    ],
  },

  // ─── Access (Histórico) ───
  { path: '/access-database', label: 'Access (Histórico)', icon: Database, type: 'single' },

  // ─── Administración (NUEVO: agrupa los items admin que estaban sueltos al tope) ───
  {
    id: 'administracion',
    label: 'Administración',
    icon: ShieldAlert,
    type: 'module',
    adminOnly: true,
    subItems: [
      { path: '/admin/calidad', label: 'Calidad de Código', icon: ShieldCheck, group: 'Calidad / Salud' },
      { path: '/admin/code-health', label: 'Code Health Dashboard', icon: HeartPulse, group: 'Calidad / Salud' },
      { path: '/admin/tarjetas-reporte', label: 'Reporte por Tarjeta', icon: CreditCard, group: 'Tarjetas y Cajas' },
      { path: '/admin/tarjetas-conciliacion', label: 'Conciliación Cartolas', icon: CreditCard, group: 'Tarjetas y Cajas' },
      { path: '/admin/cajas-rendir', label: 'Cajas a Rendir', icon: Wallet, group: 'Tarjetas y Cajas' },
      { path: '/admin/cajas-rendir-reporte', label: 'Reporte Cajas', icon: Wallet, group: 'Tarjetas y Cajas' },
    ],
  },

  // ─── Configuración ───
  {
    id: 'configuracion',
    path: '/configuracion', label: 'Configuración', icon: Settings, type: 'menu',
    subItems: [
      { path: '/configuracion/general', label: 'General', icon: Settings, group: 'General' },
      { path: '/configuracion/usuarios', label: 'Usuarios y Permisos', icon: Users, group: 'General' },
      { path: '/configuracion/tarjetas-pago', label: 'Tarjetas / Medios de Pago', icon: CreditCard, group: 'General' },
      { path: '/configuracion/medios-pago', label: 'Medios de Pago', icon: Wallet, group: 'General' },
      { path: '/configuracion/condiciones-pago', label: 'Condiciones de Pago', icon: CalendarClock, group: 'General' },
      { path: '/configuracion/feriados-horario', label: 'Feriados y Horario', icon: CalendarDays, group: 'General' },
      { path: '/configuracion/calendario-bancario', label: 'Calendario Bancario', icon: Landmark, group: 'General' },
      { path: '/configuracion/sync-access', label: 'Sync Access', icon: RefreshCw, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/monitoreo', label: 'Monitoreo Sistema', icon: Activity, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/health-check', label: 'Health Check ERP', icon: HeartPulse, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/cuentas-alegra', label: 'Cuentas Alegra', icon: BookOpen, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/api-ia', label: 'API para IAs (MCP)', icon: Bot, adminOnly: true, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/migracion-access-htas', label: 'Migración Access Htas', icon: Database, group: 'Sincronizaciones e Integraciones' },
      { path: '/configuracion/notificaciones', label: 'Notificaciones', icon: Mail, group: 'Notificaciones y Reportes' },
      { path: '/configuracion/recordatorios', label: 'Recordatorios Automáticos', icon: BellRing, group: 'Notificaciones y Reportes' },
      { path: '/configuracion/reportes-programados', label: 'Reportes Programados', icon: CalendarClock, adminOnly: true, group: 'Notificaciones y Reportes' },
      { path: '/configuracion/recordatorios-inventario', label: 'Recordatorios Inventario', icon: ClipboardCheck, group: 'Notificaciones y Reportes' },
      { path: '/configuracion/recordatorios-facturas-recepcion', label: 'Recordatorios Fact. Pend. Recep.', icon: BellRing, group: 'Notificaciones y Reportes' },
      { path: '/configuracion/auditoria', label: 'Auditoría', icon: Shield, group: 'Notificaciones y Reportes' },
    ],
  },
];
